import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, updateDoc, where } from "@firebase/firestore";
import { dismissLoadingToast, showErrorToast, showLoadingToast, showSuccessToast, updateLoadingToast } from "app/components/ShowToast";
import axios from "axios";
import db from "services/firebase";

interface NotionWorkspace {
  id: string;
  name: string;
  icon?: string | null;
  bot_id: string;
}

interface NotionPage {
  id: string;
  title: string;
  url: string;
  created_time: string;
  last_edited_time: string;
  archived: boolean;
  parent: {
    type: string;
    database_id?: string;
    page_id?: string;
    workspace?: boolean;
  };
  properties: any;
  workspace: NotionWorkspace;
  owner: {
    id: string;
    type: string;
    name?: string;
  };
  tenantId?: string;
  uid?: string;
  createdAt?: any;
  platform: string;
}

interface NotionDatabase {
  id: string;
  title: string;
  url: string;
  created_time: string;
  last_edited_time: string;
  parent: {
    type: string;
    page_id?: string;
    workspace?: boolean;
  };
  properties: any;
  workspace: NotionWorkspace;
  owner: {
    id: string;
    type: string;
    name?: string;
  };
  tenantId?: string;
  uid?: string;
  createdAt?: any;
  platform: string;
}

interface NotionIntegrationResponse {
  success: boolean;
  pages?: NotionPage[];
  databases?: NotionDatabase[];
  error?: string;
}

interface NotionError {
  response?: {
    status?: number;
    data?: {
      message?: string;
    };
  };
  message?: string;
}

interface CheckContentResponse {
  success: boolean;
  newPages?: NotionPage[];
  newDatabases?: NotionDatabase[];
  error?: string;
}

interface UserAuthenticationResponse {
  success: boolean;
  userId?: string;
  authenticationStatus?: {
    twoFactorEnabled?: boolean;
    ssoEnabled?: boolean;
  };
  error?: string;
}

interface NotionItem {
  owner: {
    id: string;
    type: string;
    name?: string;
  };
  last_edited_time: string;
}

const createNotionIntegration = ({
  accessToken,
}: {
  accessToken: string;
}) => {
  if (!accessToken || typeof accessToken !== 'string' || accessToken.trim() === '') {
    throw new Error('A valid access token is required for Notion integration');
  }

  const axiosInstance = axios.create({
    baseURL: 'https://api.notion.com/v1',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Notion-Version': '2022-06-28',
      Accept: 'application/json',
    },
  });

  /**
   * Helper function to map Notion API errors to user-friendly messages.
   * @param error - The error object from the Notion API.
   * @returns A user-friendly error message.
   */
  const getUserFriendlyErrorMessage = (error: unknown): string => {
    const notionError = error as NotionError;
    if (notionError.response?.status === 403) {
      return 'Access forbidden. Please check your access token permissions.';
    } else if (notionError.response?.status === 401) {
      return 'Invalid access token. Please reconnect to Notion.';
    } else if (notionError.response?.status === 404) {
      return 'Notion resource not found. Please check the workspace or resource ID.';
    } else if (notionError.response?.status === 429) {
      return 'Notion API rate limit exceeded. Please try again later.';
    }
    return (
      notionError.response?.data?.message ||
      notionError.message ||
      'An unexpected error occurred.'
    );
  };

  /**
   * Fetch all pages accessible to the integration.
   */
  const getAllNotionPages = async (): Promise<NotionIntegrationResponse> => {
    try {
      let allPages: any[] = [];
      let hasMore = true;
      let nextCursor: string | null = null;

      while (hasMore) {
        const response = await axiosInstance.post('/search', {
          filter: { value: 'page', property: 'object' },
          sort: { direction: 'ascending', timestamp: 'last_edited_time' },
          start_cursor: nextCursor,
        });

        const results = response.data.results;
        allPages = allPages.concat(results);
        hasMore = response.data.has_more;
        nextCursor = response.data.next_cursor;
      }

      const pages = allPages.map((page: any) => ({
        id: page.id,
        title: page.properties?.title?.title?.[0]?.plain_text || 'Untitled',
        url: page.url,
        created_time: page.created_time,
        last_edited_time: page.last_edited_time,
        archived: page.archived,
        parent: page.parent,
        properties: page.properties,
        workspace: {
          id: page.workspace?.id || '',
          name: page.workspace?.name || '',
          icon: page.workspace?.icon || null,
          bot_id: page.workspace?.bot_id || '',
        },
        owner: {
          id: page.owner?.id || '',
          type: page.owner?.type || 'user',
          name: page.owner?.person?.name || page.owner?.bot?.name || null,
        },
        platform: 'Notion',
      }));

      return { success: true, pages };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Fetch all databases accessible to the integration.
   */
  const getAllNotionDatabases = async (): Promise<NotionIntegrationResponse> => {
    try {
      let allDatabases: any[] = [];
      let hasMore = true;
      let nextCursor: string | null = null;

      while (hasMore) {
        const response = await axiosInstance.post('/search', {
          filter: { value: 'database', property: 'object' },
          sort: { direction: 'ascending', timestamp: 'last_edited_time' },
          start_cursor: nextCursor,
        });

        const results = response.data.results;
        allDatabases = allDatabases.concat(results);
        hasMore = response.data.has_more;
        nextCursor = response.data.next_cursor;
      }

      const databases = allDatabases.map((db: any) => ({
        id: db.id,
        title: db.title?.[0]?.plain_text || 'Untitled Database',
        url: db.url,
        created_time: db.created_time,
        last_edited_time: db.last_edited_time,
        parent: db.parent,
        properties: db.properties,
        workspace: {
          id: db.workspace?.id || '',
          name: db.workspace?.name || '',
          icon: db.workspace?.icon || null,
          bot_id: db.workspace?.bot_id || '',
        },
        owner: {
          id: db.owner?.id || '',
          type: db.owner?.type || 'user',
          name: db.owner?.person?.name || db.owner?.bot?.name || null,
        },
        platform: 'Notion',
      }));

      return { success: true, databases };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Check user authentication settings (2FA/SSO).
   * Note: Notion's API doesn't directly expose 2FA status; this is a placeholder for workspace-level checks.
   */
  const checkUserAuthentication = async (userId: string): Promise<UserAuthenticationResponse> => {
    try {
      // Notion API doesn't provide direct access to user 2FA settings
      // Instead, fetch user details to verify access
      const response = await axiosInstance.get(`/users/${userId}`);
      const user = response.data;

      // Placeholder: Assume SSO or 2FA based on workspace settings (Notion doesn't expose this directly)
      return {
        success: true,
        userId: user.id,
        authenticationStatus: {
          twoFactorEnabled: false, // Notion doesn't expose 2FA status
          ssoEnabled: user.type === 'bot' ? false : true, // Assume human users may have SSO
        },
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Check for new pages and databases not in the existing list.
   */
  const checkForNewContent = async (
    existingPages: NotionPage[] = [],
    existingDatabases: NotionDatabase[] = [],
    lastCheckTimestamp?: string
  ): Promise<CheckContentResponse> => {
    try {
      const pagesResponse = await getAllNotionPages();
      const databasesResponse = await getAllNotionDatabases();

      if (!pagesResponse.success || !databasesResponse.success) {
        return {
          success: false,
          error: pagesResponse.error || databasesResponse.error || 'Failed to fetch content',
        };
      }

      const allPages = pagesResponse.pages || [];
      const allDatabases = databasesResponse.databases || [];

      // Filter by last edited time if provided
      const filteredPages = lastCheckTimestamp
        ? allPages.filter(
            (page) => new Date(page.last_edited_time) > new Date(lastCheckTimestamp)
          )
        : allPages;
      const filteredDatabases = lastCheckTimestamp
        ? allDatabases.filter(
            (db) => new Date(db.last_edited_time) > new Date(lastCheckTimestamp)
          )
        : allDatabases;

      const existingPageIds = new Set(existingPages.map((page) => page.id));
      const existingDatabaseIds = new Set(existingDatabases.map((db) => db.id));

      const newPages = filteredPages.filter((page) => !existingPageIds.has(page.id));
      const newDatabases = filteredDatabases.filter((db) => !existingDatabaseIds.has(db.id));

      return {
        success: true,
        newPages,
        newDatabases,
      };
    } catch (error: unknown) {
      const errorMessage = getUserFriendlyErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  };

  /**
   * Save Notion integration data and assets to Firebase.
   */
  const saveNotionIntegration = async (
    data: any,
    // pages: NotionPage[],
    // databases: NotionDatabase[],
    currentUser: any,
    users:any[]
    // userAuthResults: any[]
  ) => {
    try {
      let isAnyAssetAdded = false;
      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const todaysDate = `${currentDate.getMonth() + 1}/${currentDate.getDate()}/${currentDate.getFullYear()}`;

      const tenantRef = doc(db, 'tenants', data.tenantId);
      const usersRef = collection(tenantRef, 'employees'); // Adjust collection name if different
      const websiteUsersSnapshot = await getDocs(usersRef);
      const websiteUsers = websiteUsersSnapshot.docs.map(doc => doc.data());
      const websiteEmails = new Set(websiteUsers.map(user => user.email));


      const notionRef = collection(tenantRef, 'integrations');
      const vendorsRef = collection(tenantRef, 'vendors');
      const integrationSnapshot = await getDocs(
        query(notionRef, where('name', '==', 'Notion'))
      );
      const enrichedUsers = users.map(user => ({
        ...user,
        isSecureslateUser: websiteEmails.has(user.email)
      }));

      if (integrationSnapshot.empty) {
        const newData = {
          accessToken: data.accessToken,
          name: 'Notion',
          group: 'Issue Tracking & Project Management',
          img_path: '/integrations/notion.png',
          status: 'Connected',
          connectedAt: formattedDate,
          tenantId: data.tenantId,
          uid: data.uid,
          createdAt: serverTimestamp(),
          users: users,
        };

        await addDoc(notionRef, newData);

        const vendorData = {
          authentication_type: '',
          category: 'Issue Tracking & Project Management',
          created_at: serverTimestamp(),
          due_deligence_status: 'Incomplete',
          impact_assessment: [],
          logo: [
            {
              name: 'notion_logo.png',
              size: 3395,
              url: '/integrations/notion.png',
            },
          ],
          owner: currentUser?.userName || '',
          policy_url: 'https://www.notion.so/Privacy-Policy-3468d120cf614d4c9014c09f6adc9091',
          reliance: '',
          risk_level: '',
          tenantId: data.tenantId,
          terms_url: 'https://www.notion.so/Terms-of-Service-3b8e7b2d7b0c4f7b9e6c8b6b7e6b8b6b',
          two_factor: true,
          uid: data.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'Notion',
          website_url: 'https://www.notion.so',
        //   userAuthResults,
        };

        await addDoc(vendorsRef, vendorData);
      }

      return { success: true, assetsAdded: isAnyAssetAdded };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  };

  return {
    getAllNotionPages,
    getAllNotionDatabases,
    checkUserAuthentication,
    checkForNewContent,
    saveNotionIntegration,
  };
};

export default createNotionIntegration;

export const exchangeCodeForNotionToken = async (
    code: string,
    currentUser: any,
    navigate: (path: string, options?: { replace: boolean }) => void
  ) => {
    const toastId = showLoadingToast({ message: 'Authenticating with Notion...' });
    try {
      const tokenResponse = await fetch(
        `https://us-central1-slate-134a0.cloudfunctions.net/notionOAuthCallback?code=${encodeURIComponent(code)}`,
        {
          method: 'GET',
          mode: 'cors',
          headers: {
            Accept: 'application/json',
          },
        }
      );
  
      const responseText = await tokenResponse.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error('Invalid response format from server');
      }
  
      if (!data.access_token || typeof data.access_token !== 'string' || data.access_token.trim() === '') {
        throw new Error('No valid access token received from server');
      }
  
      updateLoadingToast(toastId, {
        message: 'Token received, fetching content...',
        type: 'info',
        isLoading: true,
      });
  
      localStorage.setItem('notion_access_token', data.access_token);
      const result = await fetchAndSaveNotionContent(data.access_token, currentUser, navigate);
      dismissLoadingToast(toastId);
      showSuccessToast({ message: 'Successfully authenticated with Notion' });
  
      navigate(`/integrations?id=${result?.integrationId}&service=Notion`, { replace: true });
    } catch (error: any) {
      dismissLoadingToast(toastId);
      showErrorToast({ message: 'Notion authentication failed. Try again!' });
      navigate('/integrations', { replace: true });
      throw error;
    }
  };

  const fetchAndSaveNotionContent = async (
    accessToken: string,
    currentUser: any,
    navigate: (path: string, options?: { replace: boolean }) => void
  ): Promise<{ success: boolean; integrationId?: string; error?: string }> => {
    try {
      if (!accessToken) {
        return { success: false, error: 'No access token found' };
      }
  
      if (!currentUser || !currentUser.tenantId) {
        return { success: false, error: 'User authentication required' };
      }
  
      // Call the Firebase Function to get Notion users
      const response = await fetch(
        `https://us-central1-slate-134a0.cloudfunctions.net/getNotionUsers?access_token=${encodeURIComponent(accessToken)}&tenantId=${encodeURIComponent(currentUser?.tenantId || '')}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
  
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch Notion users');
      }
  
      let integrationId: string | undefined;
      const tenantRef = doc(db, 'tenants', currentUser.tenantId);
      const  accessReviewRef = doc(tenantRef, 'access_review', 'notion');
      const notionRef = collection(tenantRef, 'integrations');
      const vendorsRef = collection(tenantRef, 'vendors');
      const integrationSnapshot = await getDocs(
        query(notionRef, where('name', '==', 'Notion'))
      );

      const currentDate = new Date();
      const formattedDate = `${currentDate.getFullYear()}-${String(
        currentDate.getMonth() + 1
      ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;

      if (integrationSnapshot.empty) {
        const newData = {
          accessToken: accessToken,
          name: 'Notion',
          group: 'Issue Tracking & Project Management',
          img_path: '/integrations/notion.png',
          status: 'Connected',
          connectedAt: formattedDate,
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
        };
        const docRef = await addDoc(notionRef, newData);
        integrationId = docRef.id;

        const vendorData = {
          authentication_type: '',
          category: 'Issue Tracking & Project Management',
          created_at: serverTimestamp(),
          due_deligence_status: 'Incomplete',
          impact_assessment: [],
          logo: [
            {
              name: 'notion_logo.png',
              size: 3395,
              url: '/integrations/notion.png',
            },
          ],
          owner: currentUser?.userName || '',
          policy_url: 'https://www.notion.so/Privacy-Policy-3468d120cf614d4c9014c09f6adc9091',
          reliance: '',
          risk_level: '',
          tenantId: currentUser?.tenantId,
          terms_url: 'https://www.notion.so/Terms-of-Service-3b8e7b2d7b0c4f7b9e6c8b6b7e6b8b6b',
          two_factor: true,
          uid: currentUser?.uid,
          updated_at: serverTimestamp(),
          vendor_name: 'Notion',
          website_url: 'https://www.notion.so',
        //   userAuthResults,
        };
        await addDoc(vendorsRef, vendorData);
        
      }
  
      const membersMap: { [key: string]: any } = {};
      for (const user of data.users || []) {
        if (user.id) {
          if (user.type === 'bot') {
            continue;
          }
          membersMap[user.id] = {
            ...user, 
            platform: 'Notion',
            tenantId: currentUser.tenantId,
            uid: currentUser.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            status: 'active',
          };
        }
      }
  
      const notionDoc = await getDoc(accessReviewRef);
      if (notionDoc.exists()) {
        await updateDoc(accessReviewRef, {
          members: {
            ...notionDoc.data().members,
            ...membersMap,
          },
          updatedAt: serverTimestamp(),
        });
      } else {
        await setDoc(accessReviewRef, {
          platform: 'Notion',
          members: membersMap,
          tenantId: currentUser?.tenantId,
          uid: currentUser?.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
  
      return {
        success: true,
        integrationId: integrationId,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      showErrorToast({ message: 'Error fetching Notion content' });
      navigate('/integrations', { replace: true });
      return { success: false, error: errorMessage };
    }
  };